import React, { useEffect, useState } from 'react';
import { Constrain, Stack, Text, Button, Divider, Breakpoint, Clickable } from '@moda/om';
import LogoHorizontalShortIcon from '@moda/icons/logo-horizontal-short-60-x-20';
import NextIcon from '@moda/icons/next-16';
import { SwiperCarousel } from '../Swiper';
import { tc } from '../../lib/trackingContext';
import { ModaPrivateRequestModal } from './ModaPrivateRequestModal';

import './ModaPrivate.scss';

const SHOPPING_ITEMS = [
  {
    imgSrc: '/dist/public/stylist/personal_shopping1.jpg',
    imgAlt: 'personal_shopping1',
    title: 'Love Your Wardrobe',
    description:
      '360-degree wardrobing experience, including personalized lookbooks, tailored specifically to fit your style, shopping needs, and preferences.'
  },
  {
    imgSrc: '/dist/public/stylist/personal_shopping2.jpg',
    imgAlt: 'personal_shopping2',
    title: 'Exclusive Access',
    description:
      'First access to the best pieces of the season-including new arrivals, Exclusive Trunkshows, VIP Sales, and invitations connecting you directly with the designers you love.'
  },
  {
    imgSrc: '/dist/public/stylist/personal_shopping3.jpg',
    imgAlt: 'personal_shopping3',
    title: 'Special Sourcing',
    description:
      'Whether you’re looking for bespoke one-of-a-kind items, vintage, couture, or gifts, our stylists can find it.'
  },
  {
    imgSrc: '/dist/public/stylist/personal_shopping4.jpg',
    imgAlt: 'personal_shopping4',
    title: 'Luxury Service',
    description:
      'Our stylist team is here for you for any event or occasion at no cost. We will work with you from order to fitting.'
  }
];

export const ModaPrivate: React.FC = () => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [biosArray, setBiosArray] = useState<string[]>([]);
  const [shuffledImageNames, setShuffledImageNames] = useState<string[]>([]);

  const imagesFolder = 'stylist';
  const imageNames = [
    'Allie_Faitelson.png',
    'Carly_Zell.png',
    'Cassidy_McNeill.png',
    'Danielle_Chock.png',
    'Eric_Schlosberg.png',
    'Fernanda_Bravo.png',
    'Kevin_Nord.png',
    'Maggie_Stepf.png',
    'Marina_Mitkevich.png'
  ];

  type JoinTheWailtListEvent =
    | 'Moda Private Show Form Request your consultation Banner Image Clicked'
    | 'Moda Private Show Form Banner Image Clicked'
    | 'Moda Private Show Form Banner Image Mobile Clicked'
    | 'Moda Private Show Form Personal Shopping Section Clicked'
    | 'Moda Private Show Form Team Section Clicked'
    | 'Moda Private Show Form Service Section Clicked'
    | 'Moda Private Show Form Bottom Section Clicked';

  const shuffleBiosArray = (array: string[], currentIndex = 0): string[] => {
    if (currentIndex === array.length - 1) {
      return array;
    }

    const randomIndex = Math.floor(Math.random() * (array.length - currentIndex)) + currentIndex;
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];

    return shuffleBiosArray(array, currentIndex + 1);
  };

  useEffect(() => {
    const shuffledNames = shuffleBiosArray(imageNames);
    setShuffledImageNames(shuffledNames);
    const images = shuffledNames.map(imageName => `/dist/public/${imagesFolder}/${imageName}`);
    setBiosArray(images);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (text: JoinTheWailtListEvent, label: string, location: string) => {
    setShowRequestModal(true);
    tc.track(text, {
      addToTrackingEvent: {
        label: label,
        location: location
      }
    });
  };

  return (
    <>
      <div className="ModaPrivate">
        <Breakpoint gt="sm">
          <div className="ModaPrivate__image-container">
            <img
              className="ModaPrivate__image-main"
              src="/dist/public/stylist/moda_private.png"
              alt="Moda Private"
            />
            <Button
              className="ModaPrivate__button-main"
              onClick={() =>
                handleClick(
                  'Moda Private Show Form Banner Image Clicked',
                  'JOIN THE WAITLIST',
                  'Banner Image Dekstop'
                )
              }
            >
              JOIN THE WAITLIST
            </Button>
          </div>
        </Breakpoint>
        <Breakpoint lt="sm">
          <div className="ModaPrivate__image-container--mobile">
            <img
              className="ModaPrivate__image-main"
              src="/dist/public/stylist/moda_private_mobile.jpg"
              alt="Moda Private"
            />
            <Stack space={1} className="ModaPrivate__text-container">
              <Text treatment="eyebrow">Moda Private</Text>
              <LogoHorizontalShortIcon height="56px" width="150px" />
              <Stack space={4}>
                <Text treatment="h6" family="serif">
                  The ultimate personal shopping experience
                </Text>
                <Text treatment="body1">
                  Years of industry experience and unique points of view on fashion allow our team
                  of experts to elevate your shopping experience.
                </Text>
                <Button
                  className="ModaPrivate__button-main--mobile"
                  aria-label="Join the waitlist"
                  onClick={() =>
                    handleClick(
                      'Moda Private Show Form Banner Image Mobile Clicked',
                      'JOIN THE WAITLIST',
                      'Banner Image Mobile'
                    )
                  }
                >
                  JOIN THE WAITLIST
                </Button>
              </Stack>
            </Stack>
          </div>
        </Breakpoint>
        <Constrain className="ModaPrivate__constrain">
          <Stack space={5} className="ModaPrivate__personal-shopping-section">
            <Stack space={0} alignItems="center">
              <div className="ModaPrivate__section-title">PERSONAL SHOPPING</div>
              <Text color="ink" treatment="body1">
                Styling available in person or virtually.
              </Text>
            </Stack>
            <Breakpoint gt="sm">
              <Stack
                space={1}
                direction="horizontal"
                justifyContent="space-evenly"
                className="ModaPrivate__personal-shopping-items"
              >
                {SHOPPING_ITEMS.map((item, index) => (
                  <Stack
                    key={index}
                    space={1}
                    alignItems="center"
                    className="ModaPrivate__personal-shopping-item"
                  >
                    <img
                      className="ModaPrivate__personal-shopping-img"
                      alt={item.imgAlt}
                      src={item.imgSrc}
                    />
                    <Text treatment="bold1" family="serif">
                      {item.title}
                    </Text>
                    <Text treatment="body1" className="ModaPrivate__personal-shopping-item-text">
                      {item.description}
                    </Text>
                  </Stack>
                ))}
              </Stack>
            </Breakpoint>
            <Breakpoint lt="sm">
              <SwiperCarousel
                identifier="ModaPrivatePersonalShopping"
                slidesPerView={1}
                pagination={{
                  enabled: false
                }}
                circleArrow
              >
                {SHOPPING_ITEMS.map((item, index) => (
                  <Stack
                    key={index}
                    space={1}
                    alignItems="center"
                    className="ModaPrivate__personal-shopping-item"
                  >
                    <img
                      className="ModaPrivate__personal-shopping-img"
                      alt={item.imgAlt}
                      src={item.imgSrc}
                    />
                    <Text treatment="bold1" family="serif">
                      {item.title}
                    </Text>
                    <Text treatment="body1" className="ModaPrivate__personal-shopping-item-text">
                      {item.description}
                    </Text>
                  </Stack>
                ))}
              </SwiperCarousel>
            </Breakpoint>
            <div className="ModaPrivate__button">
              <Button
                secondary
                onClick={() =>
                  handleClick(
                    'Moda Private Show Form Personal Shopping Section Clicked',
                    'JOIN THE WAITLIST',
                    'Personal Shopping Section'
                  )
                }
              >
                JOIN THE WAITLIST
              </Button>
            </div>
          </Stack>

          <Stack space={5} className="ModaPrivate__team-section">
            <Divider className="ModaPrivate__divider" />
            <Stack space={0}>
              <div className="ModaPrivate__section-title">OUR TEAM</div>
              <Text color="ink" treatment="body1" className="ModaPrivate__service-text">
                Explore some members of our styling team
              </Text>
            </Stack>
            <Breakpoint gt="sm">
              <SwiperCarousel
                identifier="ModaPrivateTeam"
                slidesPerGroup={3}
                slidesPerView={3}
                spaceBetween={24}
                pagination={{
                  enabled: false
                }}
                circleArrow
              >
                {biosArray.map((imgSrc, index) => (
                  <img
                    className="ModaPrivate__stylist-image"
                    key={index}
                    src={imgSrc}
                    alt={shuffledImageNames[index]}
                  />
                ))}
              </SwiperCarousel>
            </Breakpoint>
            <Breakpoint lt="sm">
              <SwiperCarousel
                identifier="ModaPrivateTeam"
                slidesPerView={1}
                spaceBetween={24}
                pagination={{
                  enabled: false
                }}
                circleArrow
              >
                {biosArray.map((imgSrc, index) => (
                  <img
                    className="ModaPrivate__stylist-image"
                    key={index}
                    src={imgSrc}
                    alt={shuffledImageNames[index]}
                  />
                ))}
              </SwiperCarousel>
            </Breakpoint>

            <div className="ModaPrivate__button">
              <Button
                secondary
                onClick={() =>
                  handleClick(
                    'Moda Private Show Form Team Section Clicked',
                    'JOIN THE WAITLIST',
                    'Team Section'
                  )
                }
              >
                JOIN THE WAITLIST
              </Button>
            </div>
          </Stack>

          <Stack space={5} className="ModaPrivate__service-section">
            <Divider className="ModaPrivate__divider" />
            <Stack space={0}>
              <div className="ModaPrivate__section-title">SERVICE</div>
              <Text color="ink" treatment="body1" className="ModaPrivate__service-text">
                À la carte luxury styling for any occasion.
              </Text>
            </Stack>
            <Stack space={0}>
              <Text color="ink" family="serif" treatment="h4">
                PRIVATE SHOPPING
              </Text>
              <Text color="ink" treatment="body1" className="ModaPrivate__service-text">
                Stylists will curate your looks based on your needs with digital lookbooks and
                personalized edits.
              </Text>
            </Stack>
            <Stack space={0}>
              <Text color="ink" family="serif" treatment="h4">
                SPECIAL OCCASION STYLING
              </Text>
              <Text color="ink" treatment="body1" className="ModaPrivate__service-text">
                Events, Bridal, Wardrobe Refresh, Red Carpet, Vacation Dressing, and Home & Holiday
                decor.
              </Text>
            </Stack>
            <Stack space={0}>
              <Text color="ink" family="serif" treatment="h4">
                GIFTING
              </Text>
              <Text color="ink" treatment="body1" className="ModaPrivate__service-text">
                Our stylists will find the perfect gift for your partner, family member, friend or
                colleague.
              </Text>
            </Stack>
            <Stack space={0}>
              <Text color="ink" family="serif" treatment="h4">
                CONSULTATION
              </Text>
              <Text color="ink" treatment="body1" className="ModaPrivate__service-text">
                Fine Jewelry, Vintage Bags, Beauty, and Home
              </Text>
            </Stack>
            <div className="ModaPrivate__button">
              <Button
                secondary
                onClick={() =>
                  handleClick(
                    'Moda Private Show Form Service Section Clicked',
                    'JOIN THE WAITLIST',
                    'Service Section'
                  )
                }
              >
                JOIN THE WAITLIST
              </Button>
            </div>
          </Stack>

          <Stack space={5} className="ModaPrivate__connect-section">
            <Divider className="ModaPrivate__divider" />
            <Stack space={0} alignItems="center">
              <div className="ModaPrivate__section-title">HOW WE CONNECT</div>
              <Text color="ink" treatment="body1">
                iMessage, Facebook Messenger, email
              </Text>
            </Stack>
            <div className="ModaPrivate__connect">
              <div className="ModaPrivate__connect_top">
                <Stack space={0} className="ModaPrivate__connect_top_text">
                  <Text color="ink" family="serif" treatment="h4">
                    ONLINE &amp; BY PHONE
                  </Text>
                  <Text color="ink" treatment="body1">
                    Whatsapp, imessage, facebook messenger, and email
                  </Text>
                </Stack>
              </div>
              <Stack space={0}>
                <Text color="ink" family="serif" treatment="h4">
                  IN PERSON
                </Text>
                <Text color="ink" treatment="body1">
                  Where available. NYC &amp; Select Locations by discretion
                </Text>
              </Stack>
            </div>
          </Stack>

          <Stack space={5} className="ModaPrivate__join-section">
            <Divider className="ModaPrivate__divider" />
            <div className="ModaPrivate__join-section-bottom">
              <img
                className="ModaPrivate__join-section-image"
                alt="Moda Private"
                src="/dist/public/stylist/Desktop_Bumper.png"
              />
              <Stack
                className="ModaPrivate__rightside-text"
                space={4}
                direction="horizontal"
                alignItems="center"
              >
                <Stack space={1}>
                  <Text treatment="body1" family="serif">
                    The ultimate personal shopping experience
                  </Text>
                  <Clickable
                    aria-label="Join the waitlist"
                    onClick={() =>
                      handleClick(
                        'Moda Private Show Form Bottom Section Clicked',
                        'JOIN THE WAITLIST',
                        'Bottom Section'
                      )
                    }
                  >
                    <Text treatment="h3" family="serif">
                      Join The WAITLIST
                    </Text>
                  </Clickable>
                </Stack>
                <Clickable onClick={() => setShowRequestModal(true)} aria-label="Join the waitlist">
                  <NextIcon height="28px" width="34px" />
                </Clickable>
              </Stack>
            </div>
          </Stack>

          <Stack space={5} className="ModaPrivate__faq-section">
            <Divider className="ModaPrivate__divider" />
            <div className="ModaPrivate__section-title">FAQs</div>
            <Stack space={4}>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  How does Moda Private Work?
                </Text>
                <Text color="cement" treatment="body1">
                  Moda Private connects you with an expert Client Advisor who will assist you in any
                  way you need. As a Moda Private Client, you'll receive weekly style edits, access
                  to private collections and events, special item sourcing, and more.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  Is there a fee associated with Moda Private?
                </Text>
                <Text color="cement" treatment="body1">
                  The service is complimentary once you are paired with a member of our styling
                  team.
                </Text>
              </Stack>

              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  How will I hear from my Private Client Advisor?
                </Text>
                <Text color="cement" treatment="body1">
                  However you would prefer! Email is our default channel, but your PCA can
                  communicate via text, WhatsApp, FaceTime, etc. If you'd like to be contacted in a
                  specific way, please don't hesitate to let us know.
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Constrain>
      </div>
      <ModaPrivateRequestModal show={showRequestModal} onClose={() => setShowRequestModal(false)} />
    </>
  );
};
